import { Component, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { ApiService } from '../../services/api.service';
import { AuthStateService } from '../../services/auth-state.service';
import { UiService } from '../../services/ui.service';
import { TranslocoService } from '@ngneat/transloco';
import { Validator } from 'projects/core/src/lib/helpers/validator';
import { AppService } from '../../services/app.service';

enum StartModes {
  Init = 'init',
  Code = 'code',
  Privacy = 'privacy',
}

@Component({
  selector: 'app-start',
  templateUrl: './start.component.html',
  styleUrls: ['./start.component.scss']
})
export class StartComponent implements OnInit {

  public type: string = null;
  public hash: string = null;
  public directhash: string = null;
  public acid: number = null;
  public mode = StartModes.Init;
  public language = 'de';

  public codeForm: UntypedFormGroup;
  public errors = null;
  public loading = false;
  public busy = false;
  public messages: Array<any> = [];
  public ppApproved = false;
  public ppPolicy: { [key: string]: string} = null;

  public StartModes = StartModes;

  private accessToken: string = null;

  constructor(
    private app: AppService,
    private api: ApiService,
    private auth: AuthStateService,
    private route: ActivatedRoute,
    private fb: UntypedFormBuilder,
    private ui: UiService,
    public transloco: TranslocoService
  ) {
    this.codeForm = this.fb.group({
      code: ['', [Validators.required]],
    });
  }

  ngOnInit(): void {
    this.transloco.setActiveLang(this.language);
    this.loading = true;
    this.route.params.subscribe(params => {
      if (params.type && params.hash) {
        if (params.directhash) {
          // access with direct url
          this.type = params.type;
          this.hash = params.hash;
          this.directhash = params.directhash;
          this.acid = params.acid || null;
          this.api.authenticateDirect(this.type, this.hash, this.directhash, this.acid).subscribe({
            next: (result: any) => {
              this.setLanguage(result);
              if (result.approved === false) {
                this.ppApproved = false;
                this.ppPolicy = result.privacypolicy;
              } else {
                this.ppApproved = true;
              }
              this.loading = false;
              this.setAuthentication(result);
            },
            error: () => {
              this.goto404();
            }
          });
        } else {
          // access with pin code
          this.type = params.type;
          this.hash = params.hash;
          this.api.checkhash(this.type, this.hash).subscribe({
            next: (result: any) => {
              this.setLanguage(result.data);
              if (result.data?.approved === false) {
                this.ppApproved = false;
                this.ppPolicy = result.data?.privacypolicy;
              } else {
                this.ppApproved = true;
              }
              this.loading = false;
            },
            error: () => {
              this.goto404();
            }
          });
        }
      } else {
        this.goto404();
      }
    });
  }

  public changeToCode(): void {
    this.codeForm.reset();
    this.mode = StartModes.Code;
  }

  public onCodeGet(showSuccess: boolean = false): void {
    this.ui.hideMessage();
    this.busy = true;
    this.api.sendpincode(this.type, this.hash).subscribe({
      next: () => {
        this.changeToCode();
        if (showSuccess === true) {
          this.ui.showMessage('success', this.transloco.translate('start.messages.pin_code_sended_again'));
        }
      },
      error: () => {
        this.ui.showMessage('error', this.transloco.translate('start.messages.pin_code_not_sended'), this.transloco.translate('common.messages.error'));
      }
    }).add(() => {
      this.busy = false;
    });
  }

  public onCodeSubmit(): void {
    this.ui.hideMessage();
    if (this.codeForm.valid === true) {
      this.api.authenticate(this.type, this.hash, this.codeForm.value.code).subscribe({
        next: (result: any) => {
          this.setAuthentication(result);
        },
        error: () => {
          this.ui.showMessage('error', this.transloco.translate('start.messages.pin_code_with_errors'), this.transloco.translate('common.messages.error'));
        }
      });
    } else {
      this.ui.showMessage('error', this.transloco.translate('start.messages.please_enter_pin_code'), this.transloco.translate('common.messages.error'));
    }
  }

  public onAcceptPrivacyPolicy(): void {
    this.api.acceptPrivacyPolicy(this.hash).subscribe({
      next: () => {
        this.auth.login(this.accessToken, 'dashboard');
      },
      error: () => {
        this.ui.showMessage('error', this.transloco.translate('common.messages.error_happened'));
        this.mode = StartModes.Init;
      }
    });
  }

  private setAuthentication(result: any): void {
    if (this.ppApproved === true) {
      this.auth.login(result.access_token, 'dashboard');
    } else {
      this.accessToken = result.access_token;
      this.mode = StartModes.Privacy;
    }
  }

  private goto404(): void {
    this.app.set404();
  }

  private setLanguage(data: any): void {
    if (Validator.isString(data?.language)) {
      this.language = data.language.toLowerCase();
      this.transloco.setActiveLang(this.language);
    }
  }

}
